import React, { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import TagInput from './TagInput'
import FolderSelect from './FolderSelect'
import { Tag } from '@/types/bookmark'
import { userAccountApi } from '../../../../shared/services/userAccountApi'
import { bookmarkApi } from '../../../../shared/services/bookmarkApi'
import { useTranslation } from 'react-i18next'

interface CreateUrlModalProps {
  isOpen: boolean
  onClose: () => void
  onUrlCreated?: (url: any) => void
  selectedFolder?: string | null
}

const CreateUrlModal: React.FC<CreateUrlModalProps> = ({
  isOpen,
  onClose,
  onUrlCreated,
  selectedFolder = null,
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    originalUrl: '',
    customCode: '',
    title: '',
    folderId: selectedFolder,
    tags: [] as Tag[],
    notes: '',
    password: '', // 비밀번호 보호 (Growth 플랜 이상)
    expiresAt: '', // 만료 날짜 (Growth 플랜 이상)
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [userPlan, setUserPlan] = useState<{
    plan: string
    features: {
      passwordProtection: boolean
      expirationDates: boolean
    }
  } | null>(null)

  // selectedFolder가 변경될 때 formData 업데이트
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      folderId: selectedFolder,
    }))
  }, [selectedFolder])

  // 사용자 플랜 정보 가져오기
  useEffect(() => {
    if (isOpen) {
      fetchUserPlan()
    }
  }, [isOpen])

  const fetchUserPlan = async () => {
    try {
      const subscription = await userAccountApi.getSubscription<{
        currentPlan?: { type?: string }
        features?: { passwordProtection: boolean; expirationDates: boolean }
      }>()

      setUserPlan({
        plan: subscription?.currentPlan?.type || 'free',
        features: subscription?.features || {
          passwordProtection: false,
          expirationDates: false,
        },
      })
    } catch (error) {
      console.error('플랜 정보 조회 실패:', error)
    }
  }

  if (!isOpen) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const requestData: any = {
        originalUrl: formData.originalUrl,
        title: formData.title,
        tags: formData.tags.map(tag => tag.name), // ObjectId 대신 이름 문자열 배열로 변경
        notes: formData.notes,
      }

      // customCode가 빈 문자열이 아닐 때만 포함
      if (formData.customCode && formData.customCode.trim()) {
        requestData.customCode = formData.customCode.trim()
      }

      // folderId가 null이 아닐 때만 포함
      if (formData.folderId) {
        requestData.folderId = formData.folderId
      }

      // 비밀번호가 설정된 경우 추가 (Growth 플랜 이상)
      if (formData.password && formData.password.trim()) {
        requestData.password = formData.password.trim()
      }

      // 만료날짜가 설정된 경우 추가 (Growth 플랜 이상)
      if (formData.expiresAt && formData.expiresAt.trim()) {
        requestData.expiresAt = new Date(formData.expiresAt).toISOString()
      }

      const data = await bookmarkApi.create(requestData)
      if (data.success) {
        onUrlCreated?.(data.data)
        onClose()
        // 폼 리셋
        setFormData({
          originalUrl: '',
          customCode: '',
          title: '',
          folderId: selectedFolder,
          tags: [],
          notes: '',
          password: '',
          expiresAt: '',
        })
      } else {
        setError(data.message || t('form.createError'))
      }
    } catch (err) {
      // 검증 오류 메시지는 API 클라이언트가 합쳐서 전달한다
      setError(err instanceof Error ? err.message : t('form.networkError'))
    } finally {
      setLoading(false)
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('form.createLink')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* {t('form.originalUrl')} */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.originalUrl')} *
            </label>
            <input
              type="url"
              required
              value={formData.originalUrl}
              onChange={e =>
                setFormData({ ...formData, originalUrl: e.target.value })
              }
              placeholder="https://example.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* 제목 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.title')}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={e =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder={t('form.titleOptional')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* {t('form.customCode')} */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.customCode')}
            </label>
            <div className="flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-400">
                https://shorty.kr/
              </span>
              <input
                type="text"
                value={formData.customCode}
                onChange={e =>
                  setFormData({ ...formData, customCode: e.target.value })
                }
                placeholder="custom-link"
                className="flex-1 rounded-r-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('form.customCodeHint')}
            </p>
          </div>

          {/* 폴더 선택 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.folder')}
            </label>
            <FolderSelect
              value={formData.folderId}
              onChange={folderId => setFormData({ ...formData, folderId })}
            />
            {/* 디버깅을 위한 임시 표시 */}
            <div className="mt-1 text-xs text-gray-500">
              {t('form.currentFolder', {
                name: formData.folderId || t('form.none'),
              })}
            </div>
          </div>

          {/* 태그 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.tags')}
            </label>
            <TagInput
              value={formData.tags}
              onChange={tags => setFormData({ ...formData, tags })}
              placeholder={t('form.tagsPlaceholder')}
            />
          </div>

          {/* 메모 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="이 링크에 대한 메모..."
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Growth 플랜 이상 전용 기능 */}
          {userPlan && (
            <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-4 flex items-center text-lg font-medium text-gray-900 dark:text-white">
                <span className="mr-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 px-2 py-1 text-xs text-white">
                  {userPlan.plan === 'free'
                    ? 'Growth+'
                    : userPlan.plan.toUpperCase()}
                </span>
                {t('form.advanced')}
              </h3>

              {/* {t('form.passwordProtect')} */}
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('form.passwordProtect')}
                  {!userPlan.features.passwordProtection && (
                    <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">
                      {t('form.planRequired')}
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder={
                    userPlan.features.passwordProtection
                      ? t('form.passwordPlaceholder')
                      : t('form.planAvailable')
                  }
                  disabled={!userPlan.features.passwordProtection}
                  className={`w-full rounded-md border px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                    userPlan.features.passwordProtection
                      ? 'border-gray-300 dark:border-gray-600'
                      : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800'
                  }`}
                />
                {!userPlan.features.passwordProtection && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <a
                      href="/price"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Growth 플랜으로 업그레이드
                    </a>
                    하여 {t('form.passwordProtect')} 기능을 사용하세요.
                  </p>
                )}
              </div>

              {/* 만료 날짜 */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  만료 날짜
                  {!userPlan.features.expirationDates && (
                    <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">
                      {t('form.planRequired')}
                    </span>
                  )}
                </label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={e =>
                    setFormData({ ...formData, expiresAt: e.target.value })
                  }
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} // 1분 후부터 설정 가능
                  disabled={!userPlan.features.expirationDates}
                  className={`w-full rounded-md border px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                    userPlan.features.expirationDates
                      ? 'border-gray-300 dark:border-gray-600'
                      : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800'
                  }`}
                />
                {!userPlan.features.expirationDates ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <a
                      href="/price"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Growth 플랜으로 업그레이드
                    </a>
                    하여 만료 날짜 설정 기능을 사용하세요.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('form.expiryHint')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* 버튼들 */}
          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.originalUrl}
              className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? t('form.creating') : t('form.createSubmit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateUrlModal
